import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { NgbActiveModal, NgbModal, NgbAccordionDirective, NgbAccordionItem, NgbAccordionHeader, NgbAccordionToggle, NgbAccordionButton, NgbCollapse, NgbAccordionCollapse, NgbAccordionBody } from '@ng-bootstrap/ng-bootstrap';
import { FeedbackService } from '../../services/feedback.service';
import * as requests from '../../requests';
import { Feedback } from '../../models/feedback';
import { ModalService } from 'src/app/services/modal.service';
import { CreateFeedbackModalComponent } from '../create-feedback-modal/create-feedback-modal.component';
import { PlanogramService } from 'src/app/services/planogram.service';
import { ImageService } from 'src/app/services/image.service';
import { FileDownloadService } from 'src/app/services/file-download.service';
import { ImagePreviewModalComponent } from '../image-preview-modal/image-preview-modal.component';
import { DialogService } from 'src/app/services/dialog.service';
import { AuthService } from 'src/app/services/auth.service';
import { SafeUrl } from '@angular/platform-browser';
import { EventsService } from 'src/app/services/events.service';
import { RolesService } from 'src/app/services/roles.service';
import { SystemRole } from 'src/app/models/roles';
import { EventType } from 'src/app/models/events';
import { NgIf, NgTemplateOutlet, NgFor, DatePipe } from '@angular/common';

@Component({
    selector: 'app-planogram-feedback-modal',
    templateUrl: './planogram-feedback-modal.component.html',
    styleUrls: ['./planogram-feedback-modal.component.scss'],
    encapsulation: ViewEncapsulation.None,
    imports: [
        NgIf,
        NgbAccordionDirective,
        NgbAccordionItem,
        NgbAccordionHeader,
        NgbAccordionToggle,
        NgbAccordionButton,
        NgbCollapse,
        NgbAccordionCollapse,
        NgbAccordionBody,
        NgTemplateOutlet,
        NgFor,
        DatePipe,
    ],
})
export class PlanogramFeedbackModalComponent implements OnInit, OnDestroy {
  feedbackList: Feedback[] = [];
  feedbackTree: Feedback[] = [];
  groupedFeedback: Record<string, Feedback[]> = {};
  loading = false;
  thumbnailUrls: Map<string, SafeUrl> = new Map();
  currentUserId: string | null = null;
  otherUsers: string[] = [];

  constructor(
    public activeModal: NgbActiveModal,
    private modalService: ModalService,
    private dialogService: DialogService,
    private planogramService: PlanogramService,
    private feedbackService: FeedbackService,
    private rolesService: RolesService,
    private eventsService: EventsService,
    private imageService: ImageService,
    private ngbModal: NgbModal,
    private authService: AuthService,
    private fileDownloadService: FileDownloadService,
  ) {}

  ngOnInit(): void {
    this.currentUserId = this.authService.getUserId();
    this.loadFeedback();
  }

  ngOnDestroy(): void {
    this.thumbnailUrls.forEach((url) => {
      try {
        const blobUrl = (url as any).changingThisBreaksApplicationSecurity;
        if (blobUrl) URL.revokeObjectURL(blobUrl);
      } catch {}
    });
    this.thumbnailUrls.clear();
  }

  loadFeedback(): void {
    let planogramId = this.planogramService.getPlanogram();
    let stateId = this.planogramService.getPlanogramState();
    if (!planogramId || !stateId) return;
    this.loading = true;

    const request: requests.GetFeedbackRequest = { planogramId };

    this.feedbackService.getFeedback(request).subscribe({
      next: (data) => {
        this.feedbackList = data.sort(
          (a, b) =>
            new Date(a.dateCreated).getTime() -
            new Date(b.dateCreated).getTime()
        );
        this.buildFeedbackTree();
        this.groupFeedbackByUser();

        // ✅ Track feedback viewed - only for EXTERNAL users
        if (this.rolesService.roles.value.includes(SystemRole.EXTERNAL)) {
          this.eventsService
            .createEvent({
              planogramId,
              eventType: EventType.FEEDBACK_VIEWED,
              description: 'Feedback viewed for planogram',
              metadata: planogramId
            })
            .subscribe();
        }
      },
      error: () => (this.feedbackList = []),
      complete: () => (this.loading = false),
    });
  }

  /** Build a nested feedback tree so replies appear under their parent. */
  private buildFeedbackTree(): void {
    const map = new Map<string, Feedback & { replies?: Feedback[] }>();
    this.feedbackList.forEach((fb) => map.set(fb.id, { ...fb, replies: [] }));

    this.feedbackTree = [];

    for (const fb of map.values()) {
      if (fb.repliedTo && map.has(fb.repliedTo)) {
        map.get(fb.repliedTo)!.replies!.push(fb);
      } else {
        this.feedbackTree.push(fb);
      }
    }

    // sort replies chronologically
    const sortRecursive = (list: Feedback[]) => {
      list.sort(
        (a, b) =>
          new Date(a.dateCreated).getTime() - new Date(b.dateCreated).getTime()
      );
      list.forEach((fb: any) => fb.replies && sortRecursive(fb.replies));
    };
    sortRecursive(this.feedbackTree);
  }

  private rebuildFeedbackTreeSorted(): void {
    this.feedbackList.sort(
      (a, b) =>
        new Date(a.dateCreated).getTime() - new Date(b.dateCreated).getTime()
    );
    this.buildFeedbackTree();
    this.groupFeedbackByUser();
  }

  /** Group feedback threads by user for accordion display */
  private groupFeedbackByUser(): void {
    this.groupedFeedback = {};
    this.otherUsers = [];

    for (const feedback of this.feedbackTree) {
      const isMine = feedback.userId === this.currentUserId;
      const key = isMine ? 'My Feedback' : feedback.email;

      if (!this.groupedFeedback[key]) {
        this.groupedFeedback[key] = [];
        if (!isMine && !this.otherUsers.includes(feedback.email)) {
          this.otherUsers.push(feedback.email);
        }
      }

      this.groupedFeedback[key].push(feedback);
    }
  }

  addFeedback(): void {
    this.modalService
      .openModalWithOptions(CreateFeedbackModalComponent, {
        centered: true,
        size: 'xl',
      })
      .then((newFeedback: Feedback) => {
        if (newFeedback) {
          this.feedbackList.push(newFeedback);
          this.rebuildFeedbackTreeSorted();
        }
      })
      .catch(() => {});
  }

  isImage(filename: string): boolean {
    const ext = filename.split('.').pop()?.toLowerCase();
    return ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(ext ?? '');
  }

  async getThumbnail(att: {
    filename: string;
    url: string;
    attachmentId: string;
  }): Promise<SafeUrl> {
    if (this.thumbnailUrls.has(att.attachmentId)) {
      return this.thumbnailUrls.get(att.attachmentId)!;
    }

    if (this.isImage(att.filename)) {
      const blob = await fetch(att.url, { mode: 'cors' }).then((res) =>
        res.blob()
      );
      const safeUrl = this.imageService['createImageUrl'](
        blob,
        att.attachmentId
      );
      this.thumbnailUrls.set(att.attachmentId, safeUrl);
      return safeUrl;
    }

    return this.imageService['missingImageUrl'];
  }

  openImage(att: { filename: string; url: string }): void {
    const modalRef = this.ngbModal.open(ImagePreviewModalComponent, {
      centered: true,
      size: 'xl',
      backdrop: 'static',
    });
    modalRef.componentInstance.imageUrl = att.url;
    modalRef.componentInstance.filename = att.filename;
  }

  downloadAttachment(att: { url: string; filename: string }): void {
    // Try to force a file download using fetch and blob
    fetch(att.url)
      .then((res) => res.blob())
      .then((blob) => this.fileDownloadService.triggerDownload(blob, att.filename))
      .catch(() => {
        // fallback: open in new tab if direct download fails
        window.open(att.url, '_blank');
      });
  }

  onAttachmentsShown(fb: Feedback): void {
    if (fb.attachments?.length) {
      for (const att of fb.attachments) {
        if (
          this.isImage(att.filename) &&
          !this.thumbnailUrls.has(att.attachmentId)
        ) {
          this.getThumbnail(att);
        }
      }
    }
  }

  replyToFeedback(parentFeedbackId: string): void {
    const modalRef = this.ngbModal.open(CreateFeedbackModalComponent, {
      centered: true,
      size: 'xl',
    });
    modalRef.componentInstance.feedbackId = parentFeedbackId;

    modalRef.result
      .then((reply: Feedback) => {
        if (reply) {
          this.feedbackList.push(reply);
          this.rebuildFeedbackTreeSorted();
        }
      })
      .catch(() => {});
  }

  async deleteFeedback(feedbackId: string): Promise<void> {
    const confirmed = await this.dialogService.showConfirmation(
      'Delete Feedback',
      'Are you sure you want to delete this feedback?'
    );
    if (!confirmed) return;

    const planogramId = this.planogramService.getPlanogram();

    this.feedbackService.deleteFeedback(feedbackId).subscribe((success) => {
      if (success) {
        this.feedbackList = this.feedbackList.filter(
          (fb) => fb.id !== feedbackId && fb.repliedTo !== feedbackId
        );
        this.rebuildFeedbackTreeSorted();
        this.dialogService.showSuccess('Feedback deleted successfully.');

        // ✅ Track event (External users only)
        if (
          planogramId &&
          this.rolesService.roles.value.includes(SystemRole.EXTERNAL)
        ) {
          this.eventsService
            .createEvent({
              planogramId,
              eventType: EventType.FEEDBACK_DELETED,
              description: 'User deleted feedback.',
              metadata: feedbackId
            })
            .subscribe();
        }
      } else {
        this.dialogService.showError('Failed to delete feedback.');
      }
    });
  }

  async deleteAttachment(attachmentId: string, fb: Feedback): Promise<void> {
    const confirmed = await this.dialogService.showConfirmation(
      'Remove Attachment',
      'Remove this attachment?'
    );
    if (!confirmed) return;

    const planogramId = this.planogramService.getPlanogram();

    this.feedbackService.deleteAttachment(attachmentId).subscribe((success) => {
      if (success && fb.attachments) {
        fb.attachments = fb.attachments.filter(
          (a) => a.attachmentId !== attachmentId
        );
        this.thumbnailUrls.delete(attachmentId);
        this.dialogService.showSuccess('Attachment removed.');

        // ✅ Track event (External users only)
        if (
          planogramId &&
          this.rolesService.roles.value.includes(SystemRole.EXTERNAL)
        ) {
          this.eventsService
            .createEvent({
              planogramId,
              eventType: EventType.FEEDBACK_ATTACHMENTS_REMOVED,
              description: 'User removed feedback attachment.',
              metadata: attachmentId
            })
            .subscribe();
        }
      } else {
        this.dialogService.showError('Failed to remove attachment.');
      }
    });
  }

  close(): void {
    this.activeModal.dismiss();
  }
}
